import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import type { ExternalIdentifier, Publisher } from 'types/writer'

import { editPublisher } from '@disco/state-management/actions/rights-data-actions'
import { useGetRightsHolderByIdQuery } from '@disco/state-management/api/rights-holders'
import { updateSplit } from '@disco/state-management/reducers/rightsManagement'
import { getPublishers } from '@disco/state-management/selectors/contacts'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { BaseDialog } from '../../components/base-dialog'
import { useRightsNewModelEnabled, useUpdateRightsEntity } from '../../hooks'
import type { RightsDataModalLocationState } from '../../types'
import { findDuplicateName } from '../../utils/validation'

/**
 * EditPublisherModal - Modal for editing an existing publisher
 *
 * This modal allows users to edit a publisher's name, IPI, and ISNI.
 * The publisher ID is extracted from the route params.
 *
 * When edited from a split row (via splitId and splitsKey in location state),
 * the split is automatically updated with the edited publisher to ensure it
 * remains selected in the dropdown.
 */
export function EditPublisherModal() {
  const dispatch = useDispatch()
  const location = useLocation()
  const { publisherId } = useParams<{ publisherId: string }>()
  const { splitId, splitsKey } =
    (location.state as RightsDataModalLocationState) || {}

  const rightsNewModelEnabled = useRightsNewModelEnabled()

  // New API: fetch by ID from route param
  const { data: rightsHolderData } = useGetRightsHolderByIdQuery(
    publisherId ? parseInt(publisherId, 10) : 0,
    { skip: !rightsNewModelEnabled || !publisherId },
  )

  // Old API: lookup from Redux
  const publishers = useSelector(getPublishers)
  const publisherFromRedux = publishers.find(
    (p: Publisher) => p.id.toString() === publisherId,
  )

  // Use new API data or fall back to old Redux data
  const publisher: Publisher | undefined = useMemo(() => {
    if (rightsNewModelEnabled) {
      return rightsHolderData
        ? {
            id: rightsHolderData.id,
            name: rightsHolderData.title,
            external_identifiers: rightsHolderData.external_identifiers,
          }
        : undefined
    }
    return publisherFromRedux
  }, [rightsNewModelEnabled, rightsHolderData, publisherFromRedux])

  const [inputValue, setInputValue] = useState(publisher?.name || '')

  // Sync inputValue when publisher changes (e.g., async load)
  useEffect(() => {
    if (publisher?.name) {
      setInputValue(publisher.name)
    }
  }, [publisher?.name])

  const duplicatePublisherName = useMemo(
    () =>
      publisher
        ? findDuplicateName(inputValue, publishers, publisher.id)
        : null,
    [inputValue, publishers, publisher],
  )

  const warningMessage = duplicatePublisherName
    ? `${duplicatePublisherName} already exists. Please double-check if this is a duplicate before saving.`
    : null

  const handleNameChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const updateEntity = useUpdateRightsEntity({
    entityType: 'Publisher',
    updateThunk: editPublisher,
  })

  const handleSave = useCallback(
    async (
      name: string,
      externalIdentifiers: ExternalIdentifier[] | undefined,
    ) => {
      if (!publisher) return

      await updateEntity(
        publisher.id,
        name,
        externalIdentifiers,
        publisher.external_identifiers,
      )

      // Auto-select the edited publisher in the split that triggered the edit
      if (splitId && splitsKey) {
        dispatch(
          updateSplit({
            trackId: splitsKey,
            splitId,
            changes: { publisherId: publisher.id },
          }),
        )
      }
    },
    [publisher, updateEntity, dispatch, splitId, splitsKey],
  )

  // Don't render if publisher not found
  if (!publisher) {
    return null
  }

  return (
    <BaseDialog
      heading="Edit publisher"
      nameFieldLabel="Publisher name"
      initialName={publisher.name}
      initialIdentifiers={publisher.external_identifiers}
      helperText="Editing this publisher will update it everywhere it's used."
      onSave={handleSave}
      isEditMode={true}
      warningMessage={warningMessage}
      onNameChange={handleNameChange}
      showIPI
      showISNI
      open
    />
  )
}

export default EditPublisherModal
